import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, IconButton, Tooltip, TextField
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ApiServices from '../../Utils/ApiServices/ApiServices';

const AddMemberDialog = ({
  open,
  onClose,
  onReferralCreated,
  accessToken,
  LoginUserType,
  plan_id,
  groupId,
  setSnackbar
}) => {
  const [referralData, setReferralData] = React.useState(null);
  const [referralDialogOpen, setReferralDialogOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddMember = async () => {
    if (!email || !emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    const postUrl =
      LoginUserType === 'Patient'
        ? `/apis/default/portal/membershipreferral/${plan_id}/membership/${groupId}`
        : `/apis/default/api/membershipreferral/${plan_id}/membership/${groupId}`;

    try {
      const res = await ApiServices.post(
        postUrl,
        { member_email: email },
        { Authorization: `Bearer ${accessToken}` }
      );

      setReferralData({ ...res.data, email });
      setReferralDialogOpen(true);
      onReferralCreated();
      setSnackbar({ open: true, message: 'Referral key created successfully', severity: 'success' });
    } catch (err) {
      console.error('Referral Key Creation Error:', err);
      setSnackbar({ open: true, message: 'Failed to create referral key', severity: 'error' });
    } finally {
      setIsSubmitting(false);
      setEmail('');
      setEmailError('');
      onClose();
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'info' });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Invite Member via Email</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Enter the email ID of the user you want to add as a member to this Membership Plan:
          </Typography>
          <TextField
            label="Member Email"
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError}
            margin="normal"
            type="email"
            autoFocus
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Generating...' : 'Generate Referral Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Referral Key Dialog */}
      <Dialog open={referralDialogOpen} onClose={() => setReferralDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Referral Key Generated</DialogTitle>
        <DialogContent dividers>
          {referralData && (
            <>
              <Typography variant="body1" mb={2}>
                We have sent the referral key to <strong>{referralData.email}</strong>. You can also copy it from here:
              </Typography>

              <Box
                sx={{
                  border: '2px dotted #028391',
                  borderRadius: 2,
                  backgroundColor: '#f1fdfd',
                  padding: 2,
                  mt: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ color: '#028391', fontWeight: 'bold', mb: 1 }}>
                  Referral Key
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {referralData.referral_key}
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={() => handleCopy(referralData.referral_key)} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReferralDialogOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddMemberDialog;
